import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Box, Paper, Typography, TextField, Button, Alert, CircularProgress, IconButton, InputAdornment } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { confirmPasswordReset } from '../api/api';

function getPasswordStrength(password: string) {
  const minLength = password.length >= 8;
  const hasNumber = /[0-9]/.test(password);
  const hasUpper = /[A-Z]/.test(password);
  const hasLower = /[a-z]/.test(password);
  const hasSpecial = /[^A-Za-z0-9]/.test(password);
  return { minLength, hasNumber, hasUpper, hasLower, hasSpecial };
}

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const strength = getPasswordStrength(password);
  const allValid = Object.values(strength).every(Boolean);
  const passwordsMatch = password === confirm && password.length > 0;

  const handleReset = async () => {
    setStatus('loading');
    setMessage('');
    try {
      const res = await confirmPasswordReset(token, password);
      setStatus((res.data as any).success ? 'success' : 'error');
      setMessage((res.data as any).message);
    } catch (err: any) {
      setStatus('error');
      setMessage(err?.response?.data?.message || 'Failed to reset password.');
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', p: 2 }}>
      <Paper elevation={8} sx={{ p: 4, borderRadius: 3, minWidth: 350, maxWidth: 400, textAlign: 'center' }}>
        <Box sx={{ mb: 2 }}>
          <img src="/static/logos/cvision_logo.svg" alt="CVision Logo" style={{ width: 48, height: 48, marginBottom: 8 }} />
        </Box>
        <Typography variant="h5" fontWeight={700} gutterBottom>Reset Password</Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Enter your new password below. Password must be at least 8 characters and meet all requirements.
        </Typography>
        <TextField
          label="New Password"
          type={showPassword ? 'text' : 'password'}
          fullWidth
          margin="normal"
          value={password}
          onChange={e => setPassword(e.target.value)}
          disabled={status === 'loading' || status === 'success'}
          aria-label="New password"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  onClick={() => setShowPassword(v => !v)}
                  edge="end"
                  disabled={status === 'loading' || status === 'success'}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            )
          }}
          helperText={
            !allValid && password.length > 0 ?
              <Box component="span" sx={{ color: 'error.main', fontSize: 13 }}>
                Password must have:
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
                  <li style={{ color: strength.minLength ? 'green' : undefined }}>At least 8 characters</li>
                  <li style={{ color: strength.hasNumber ? 'green' : undefined }}>A number</li>
                  <li style={{ color: strength.hasUpper ? 'green' : undefined }}>An uppercase letter</li>
                  <li style={{ color: strength.hasLower ? 'green' : undefined }}>A lowercase letter</li>
                  <li style={{ color: strength.hasSpecial ? 'green' : undefined }}>A special character</li>
                </ul>
              </Box>
            : ' '
          }
        />
        <TextField
          label="Confirm Password"
          type={showConfirm ? 'text' : 'password'}
          fullWidth
          margin="normal"
          value={confirm}
          onChange={e => setConfirm(e.target.value)}
          disabled={status === 'loading' || status === 'success'}
          aria-label="Confirm password"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label={showConfirm ? 'Hide password' : 'Show password'}
                  onClick={() => setShowConfirm(v => !v)}
                  edge="end"
                  disabled={status === 'loading' || status === 'success'}
                >
                  {showConfirm ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            )
          }}
          error={confirm.length > 0 && !passwordsMatch}
          helperText={confirm.length > 0 && !passwordsMatch ? 'Passwords do not match' : ' '}
        />
        {message && (
          <Alert severity={status === 'success' ? 'success' : 'error'} sx={{ mt: 2 }}>{message}</Alert>
        )}
        <Button
          fullWidth
          variant="contained"
          sx={{ mt: 2, py: 1.2, fontWeight: 600, borderRadius: 2 }}
          disabled={!allValid || !passwordsMatch || status === 'loading' || status === 'success'}
          onClick={handleReset}
          aria-label="Reset password"
        >
          {status === 'loading' ? <CircularProgress size={22} /> : 'Reset Password'}
        </Button>
        {status === 'success' && (
          <Button fullWidth sx={{ mt: 2 }} onClick={() => navigate('/login')} variant="outlined">Back to Login</Button>
        )}
      </Paper>
    </Box>
  );
};

export default ResetPassword; 